/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { OpeningActivities } from '../models/OpeningActivities';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class OpeningsService {

    /**
     * List Opening Activities
     * Fetches the audit trail (version history) for a specific opening with pagination
     * @param id Opening ID (external_id)
     * @param page Page number (default: 1)
     * @param perPage Number of items per page (default: 100, max: 100)
     * @returns OpeningActivities OK
     * @throws ApiError
     */
    public static getInternalApiOpeningsActivities(
        id: string,
        page?: number,
        perPage?: number,
    ): CancelablePromise<OpeningActivities> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/openings/{id}/activities',
            path: {
                'id': id,
            },
            query: {
                'page': page,
                'per_page': perPage,
            },
            errors: {
                401: `Unauthorized`,
                404: `Not Found`,
            },
        });
    }

    /**
     * Cancel Opening Approval Request
     * Cancels an opening approval request and sets the opening status to approved
     * @param id Opening ID
     * @returns void
     * @throws ApiError
     */
    public static deleteInternalApiOpeningsApprovals(
        id: string,
    ): CancelablePromise<void> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/internal_api/openings/{id}/approvals',
            path: {
                'id': id,
            },
            errors: {
                401: `Unauthorized`,
                404: `Not Found`,
                422: `Unprocessable Entity`,
            },
        });
    }

    /**
     * Create Opening Approval Request
     * Creates an opening approval request
     * @param id Opening ID
     * @param requestBody
     * @returns any Created
     * @throws ApiError
     */
    public static postInternalApiOpeningsApprovals(
        id: string,
        requestBody?: {
            hiring_goals_attributes: Array<{
                goal: number;
                hire_by?: string | null;
                start_date?: string | null;
                form_index?: number | null;
            }>;
            request_reason: string;
        },
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/internal_api/openings/{id}/approvals',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Unprocessable Entity`,
            },
        });
    }

    /**
     * Request download of Opening CSV
     * Enqueues a job and returns status code only
     * @returns void
     * @throws ApiError
     */
    public static getInternalApiOpeningsCsvs(): CancelablePromise<void> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/openings/csvs',
            errors: {
                401: `Unauthorized`,
            },
        });
    }

    /**
     * Upload CSV to update openings
     * Updates openings via uploaded CSV.
     * @param requestBody csv
     * @returns void
     * @throws ApiError
     */
    public static postInternalApiOpeningsCsvs(
        requestBody?: string,
    ): CancelablePromise<void> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/internal_api/openings/csvs',
            body: requestBody,
            mediaType: 'text/csv',
            errors: {
                400: `Bad File`,
                401: `Unauthorized`,
            },
        });
    }

    /**
     * Export an Opening
     * Returns a JSON representation of the opening including metadata and references to its position, location, and workflow by external ID. Compatible with POST /internal_api/openings/import.
     * @param id External ID of the opening to export
     * @param xAccessToken
     * @returns any OK
     * @throws ApiError
     */
    public static getInternalApiOpeningsExport(
        id: string,
        xAccessToken: string,
    ): CancelablePromise<{
        opening?: {
            title?: string;
            active?: boolean;
            is_private?: boolean;
            is_hiring_funnel?: boolean;
            is_sourcing_funnel?: boolean;
            description?: string | null;
            external_title?: string | null;
            compensation?: string | null;
            compensation_min?: number | null;
            compensation_max?: number | null;
            compensation_type?: string | null;
            compensation_currency_code?: string | null;
            job_type?: string | null;
            job_hours?: string | null;
            job_industry?: string | null;
            position_external_id?: string | null;
            location_external_id?: string | null;
            workflow_external_id?: string | null;
            shift?: {
                external_id?: string | null;
                name?: string | null;
                description?: string | null;
                days_of_week?: Array<string>;
                hours?: any;
                weekly_hour_slots?: any;
                min_weekly_hours?: number | null;
                max_weekly_hours?: number | null;
            } | null;
        };
        exported_at?: string;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/openings/{id}/export',
            path: {
                'id': id,
            },
            headers: {
                'X-ACCESS-TOKEN': xAccessToken,
            },
            errors: {
                401: `Unauthorized`,
                404: `Not Found`,
            },
        });
    }

    /**
     * Import an Opening
     * Creates an opening from a serialized export payload as returned by GET /internal_api/openings/{id}/export.
     * @param xAccessToken
     * @param requestBody
     * @returns any Created
     * @throws ApiError
     */
    public static postInternalApiOpeningsImport(
        xAccessToken: string,
        requestBody: any,
    ): CancelablePromise<{
        id?: string;
        title?: string;
        active?: boolean;
        position_external_id?: string | null;
        location_external_id?: string | null;
        workflow_external_id?: string | null;
        shift_external_id?: string | null;
    }> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/internal_api/openings/import',
            headers: {
                'X-ACCESS-TOKEN': xAccessToken,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Bad Request`,
                401: `Unauthorized`,
                422: `Unprocessable Entity`,
            },
        });
    }

    /**
     * Reassign Opening to Different Workflow (Alpha)
     * Takes a stage mapping array to move applicants from one stage to another.  The stage mapping array is an array of hashes with the keys "source" and "destination".  The "source" key is the name of the stage in the source workflow, and the "destination" key is the name of the stage in the destination workflow. This feature is in Alpha, meaning that explicit flags must be set for this endpoint to behave as expected. If you are interested in trying this API, please reach out to your account representative for more information.
     * @param requestBody
     * @returns any {"async":false}
     * @throws ApiError
     */
    public static postInternalApiOpeningsWorkflowReassignment(
        requestBody: {
            destination_workflow_id: string;
            destination_workflow_title: string | null;
            run_landing_actions: boolean;
            source_opening_id: string;
            stage_mappings: Array<{
                source_stage_id?: string;
                destination_stage_id?: string;
            }> | null;
        },
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/internal_api/openings/workflow_reassignment',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                401: `Unauthorized`,
            },
        });
    }

    /**
     * Get Targeted Openings Count for a list of Locations and Positions
     * Get count of targeted funnels for a list of locations and positions, or for a brand
     * @param xAccessToken
     * @param requestBody
     * @returns any OK
     * @throws ApiError
     */
    public static postInternalApiWxFunnelsTargetedCount(
        xAccessToken: string,
        requestBody?: {
            location_ids: Array<string>;
            position_ids: Array<string>;
            /**
             * WX brand UUID. When present, resolves all active locations under the brand and uses them as the location filter. location_ids is ignored.
             */
            brand_id?: string;
        },
    ): CancelablePromise<{
        /**
         * The count of targeted funnels for a list of locations and positions
         */
        targeted_count?: number;
    }> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/internal_api/wx/funnels/targeted_count',
            headers: {
                'X-ACCESS-TOKEN': xAccessToken,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                401: `Unauthorized`,
            },
        });
    }

}